/**
 * =============================================================================
 * SHAPE-AWARE UTILITIES
 * =============================================================================
 *
 * Utility functions for calculating clip-path paths that allow list items
 * to dynamically morph their shape to follow the container's dock cutout.
 *
 * KEY INSIGHT: Uses clip-path: path() with SVG syntax to match the exact arc
 * geometry from ContentPanels.tsx. This provides smooth curves rather than
 * polygon approximations.
 *
 * COORDINATE SYSTEMS:
 * 1. Container coordinates - Origin at top-left of .content-panels
 * 2. Item-local coordinates - Origin at top-left of the item element
 *    Transformation: itemLocalX = containerX - itemBounds.left
 *
 * ARC GEOMETRY (matching ContentPanels.tsx):
 * - Arc centers: (dockLeft + scaledCornerRadius, dockTop ± scaledCornerRadius)
 * - Arc radius: wrapRadius = scaledCornerRadius + padding
 * - Arc tangent points align with shelf/floor and leftEdge/rightEdge
 *
 * =============================================================================
 */

import {
  type CutoutGeometry,
  type PanelSide,
} from './ShapeAwareListContext';

/**
 * Configuration for dock dimensions and scaling
 */
const DOCK_CONFIG = {
  dockHoverScale: 1.06,
} as const;

/**
 * Get computed CSS variable value in pixels
 */
export function getCSSVariablePixels(variableName: string, fallback: number): number {
  const computedStyle = getComputedStyle(document.documentElement);
  const value = computedStyle.getPropertyValue(variableName).trim();
  if (!value) {return fallback;}
  if (value.endsWith('rem')) {
    return parseFloat(value) * 16;
  }
  if (value.endsWith('px')) {
    return parseFloat(value);
  }
  return fallback;
}

/**
 * Base dock dimensions (unscaled)
 */
export interface BaseDockDimensions {
  width: number;
  height: number;
  cornerRadius: number;
}

export function measureBaseDockDimensions(
  dockElement: Element,
  isDockHovered: boolean
): BaseDockDimensions {
  const dockRect = dockElement.getBoundingClientRect();
  const dockStyles = window.getComputedStyle(dockElement);
  const cornerRadius = parseFloat(dockStyles.borderRadius) || 24;

  if (isDockHovered) {
    return {
      width: dockRect.width / DOCK_CONFIG.dockHoverScale,
      height: dockRect.height / DOCK_CONFIG.dockHoverScale,
      cornerRadius: cornerRadius / DOCK_CONFIG.dockHoverScale,
    };
  }

  return { width: dockRect.width, height: dockRect.height, cornerRadius };
}

/**
 * Calculate full cutout geometry
 */
export function calculateCutoutGeometry(
  containerRect: DOMRect,
  dockRect: DOMRect,
  baseDimensions: BaseDockDimensions,
  isDockHovered: boolean
): CutoutGeometry {
  const fluidSpaceSm = getCSSVariablePixels('--fluid-space-sm', 10);
  const paddingDefault = fluidSpaceSm || 10;
  const paddingHover = paddingDefault * 0.6;

  const scale = isDockHovered ? DOCK_CONFIG.dockHoverScale : 1;
  const padding = isDockHovered ? paddingHover : paddingDefault;

  const dockCenterX = dockRect.left + dockRect.width / 2 - containerRect.left;
  const dockCenterY = dockRect.top + dockRect.height / 2 - containerRect.top;

  const dockW = baseDimensions.width * scale;
  const dockH = baseDimensions.height * scale;
  const scaledCornerRadius = baseDimensions.cornerRadius * scale;
  const dockLeft = dockCenterX - dockW / 2;
  const dockRight = dockCenterX + dockW / 2;
  const dockTop = dockCenterY - dockH / 2;
  const dockBottom = dockCenterY + dockH / 2;

  const wrapRadius = scaledCornerRadius + padding;

  const shelfY = dockTop - padding;
  const floorY = dockBottom + padding;
  const leftEdge = dockLeft - padding;
  const rightEdge = dockRight + padding;

  return {
    shelfY,
    floorY,
    leftEdge,
    rightEdge,
    wrapRadius,
    scaledCornerRadius,
    dockLeft,
    dockRight,
    dockCenterX,
    dockTop,
    dockBottom,
    containerHeight: containerRect.height,
    containerWidth: containerRect.width,
  };
}

/**
 * Item bounds in container coordinate system
 */
export interface ItemBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Result of clip-path calculation
 */
export interface ClipPathResult {
  clipPath: string;
  overlapsWithCutout: boolean;
  zone: 'above' | 'cutout' | 'below';
}

/**
 * =============================================================================
 * SVG PATH-BASED CLIP-PATH CALCULATION
 * =============================================================================
 *
 * Uses clip-path: path() with SVG syntax to create smooth arcs that exactly
 * match the container's dock cutout geometry from ContentPanels.tsx.
 *
 * The path uses pixel coordinates relative to the item element itself.
 * All container coordinates are transformed to item-local coordinates.
 *
 * Arc geometry matches ContentPanels.tsx exactly:
 *   - Arc center for top-left: (dockLeft + scaledCornerRadius, dockTop + scaledCornerRadius)
 *   - Arc radius: wrapRadius = scaledCornerRadius + padding
 *   - Arc tangent points: (dockLeft + scaledCornerRadius, shelfY) to (leftEdge, dockTop + scaledCornerRadius)
 *
 * =============================================================================
 */

/**
 * Calculate clip-path using SVG path() syntax for an item that morphs around the dock cutout
 *
 * @param itemBounds - Item position and size in container coordinates
 * @param _panelBounds - Panel position in container coordinates (unused but kept for API compatibility)
 * @param geometry - Cutout geometry
 * @param panelSide - Which panel (left = Notes, right = Gauge)
 */
export function calculateItemClipPath(
  itemBounds: ItemBounds,
  _panelBounds: { left: number; right: number },
  geometry: CutoutGeometry,
  panelSide: PanelSide
): ClipPathResult {
  const {
    shelfY,
    floorY,
    leftEdge,
    rightEdge,
    scaledCornerRadius,
    dockTop,
    dockBottom,
  } = geometry;

  const { width: itemW, height: itemH } = itemBounds;

  // Helper to convert container coordinates to item-local pixel coordinates
  const toLocalX = (containerX: number): number => containerX - itemBounds.left;
  const toLocalY = (containerY: number): number => containerY - itemBounds.top;

  // Clamp to item bounds
  const clampX = (x: number): number => Math.max(0, Math.min(itemW, x));
  const clampY = (y: number): number => Math.max(0, Math.min(itemH, y));

  // Format number for SVG path (2 decimal places)
  const fmt = (n: number): string => n.toFixed(2);

  // Check if item is completely above or below cutout zone
  if (itemBounds.bottom <= shelfY) {
    return { clipPath: '', overlapsWithCutout: false, zone: 'above' };
  }
  if (itemBounds.top >= floorY) {
    return { clipPath: '', overlapsWithCutout: false, zone: 'below' };
  }

  // Check if item actually overlaps with cutout horizontally
  if (panelSide === 'left' && itemBounds.right <= leftEdge) {
    return { clipPath: '', overlapsWithCutout: false, zone: 'above' };
  }
  if (panelSide === 'right' && itemBounds.left >= rightEdge) {
    return { clipPath: '', overlapsWithCutout: false, zone: 'above' };
  }

  // Define arc zone boundaries in container coordinates
  const topArcTopY = shelfY;
  const topArcBottomY = dockTop + scaledCornerRadius;
  const bottomArcTopY = dockBottom - scaledCornerRadius;
  const bottomArcBottomY = floorY;

  // Check which zones the item intersects
  const intersectsTopArc = itemBounds.top < topArcBottomY && itemBounds.bottom > topArcTopY;
  const intersectsStraight = itemBounds.top < bottomArcTopY && itemBounds.bottom > topArcBottomY;
  const intersectsBottomArc = itemBounds.top < bottomArcBottomY && itemBounds.bottom > bottomArcTopY;

  if (panelSide === 'left') {
    return calculateLeftPanelPath(
      itemBounds, geometry, itemW, itemH,
      toLocalX, toLocalY, clampX, clampY, fmt,
      intersectsTopArc, intersectsStraight, intersectsBottomArc,
      topArcTopY, topArcBottomY, bottomArcTopY, bottomArcBottomY
    );
  } else {
    return calculateRightPanelPath(
      itemBounds, geometry, itemW, itemH,
      toLocalX, toLocalY, clampX, clampY, fmt,
      intersectsTopArc, intersectsStraight, intersectsBottomArc,
      topArcTopY, topArcBottomY, bottomArcTopY, bottomArcBottomY
    );
  }
}

/**
 * Calculate clip-path for left panel (Notes) items
 * Clips the RIGHT side of the item to follow the cutout
 */
function calculateLeftPanelPath(
  itemBounds: ItemBounds,
  geometry: CutoutGeometry,
  itemW: number,
  itemH: number,
  toLocalX: (x: number) => number,
  toLocalY: (y: number) => number,
  clampX: (x: number) => number,
  clampY: (y: number) => number,
  fmt: (n: number) => string,
  intersectsTopArc: boolean,
  intersectsStraight: boolean,
  intersectsBottomArc: boolean,
  topArcTopY: number,
  topArcBottomY: number,
  bottomArcTopY: number,
  bottomArcBottomY: number
): ClipPathResult {
  const {
    shelfY, floorY, leftEdge, wrapRadius, scaledCornerRadius, dockLeft, dockTop, dockBottom,
  } = geometry;

  // Arc tangent points in container coordinates (matching ContentPanels.tsx)
  const topArcStartX = dockLeft + scaledCornerRadius;  // On shelf, moving toward arc
  const bottomArcEndX = dockLeft + scaledCornerRadius;  // On floor, after arc

  const pathParts: string[] = [];

  // START: Top-left corner of item
  pathParts.push(`M 0,0`);

  // TOP EDGE: Go to right side (or to where cutout starts if item starts in cutout zone)
  if (itemBounds.top < shelfY) {
    // Item starts above cutout - full width at top
    pathParts.push(`L ${fmt(itemW)},0`);

    // Go down right edge to shelf level
    const localShelfY = clampY(toLocalY(shelfY));
    pathParts.push(`L ${fmt(itemW)},${fmt(localShelfY)}`);

    // Horizontal line to top arc start point
    const localTopArcStartX = clampX(toLocalX(topArcStartX));
    pathParts.push(`L ${fmt(localTopArcStartX)},${fmt(localShelfY)}`);
  } else {
    // Item starts within cutout zone - find starting X on cutout boundary
    const startY = itemBounds.top;
    let startX: number;

    if (startY < topArcBottomY) {
      // Starting in top arc zone - calculate X on arc
      startX = getArcXAtYForPath(startY, dockLeft + scaledCornerRadius, dockTop + scaledCornerRadius, wrapRadius, 'left');
    } else if (startY > bottomArcTopY) {
      // Starting in bottom arc zone
      startX = getArcXAtYForPath(startY, dockLeft + scaledCornerRadius, dockBottom - scaledCornerRadius, wrapRadius, 'left');
    } else {
      // Starting in straight zone
      startX = leftEdge;
    }

    const localStartX = clampX(toLocalX(startX));
    pathParts.push(`L ${fmt(localStartX)},0`);
  }

  // CUTOUT EDGE: Trace along the cutout boundary with arcs

  // Top arc (if item intersects it)
  if (intersectsTopArc) {
    const arcStartY = Math.max(itemBounds.top, topArcTopY);
    const arcEndY = Math.min(itemBounds.bottom, topArcBottomY);

    // If we need to draw the arc (not just a point)
    if (arcEndY > arcStartY) {
      // Calculate start and end X positions on the arc
      const arcCenterX = dockLeft + scaledCornerRadius;
      const arcCenterY = dockTop + scaledCornerRadius;

      // Start point (if not already there from above)
      if (itemBounds.top >= topArcTopY) {
        const startX = getArcXAtYForPath(arcStartY, arcCenterX, arcCenterY, wrapRadius, 'left');
        const localStartX = clampX(toLocalX(startX));
        const localStartY = clampY(toLocalY(arcStartY));
        pathParts.push(`L ${fmt(localStartX)},${fmt(localStartY)}`);
      }

      // End point of top arc (or where item ends in arc zone)
      const endX = getArcXAtYForPath(arcEndY, arcCenterX, arcCenterY, wrapRadius, 'left');
      const localEndX = clampX(toLocalX(endX));
      const localEndY = clampY(toLocalY(arcEndY));

      // SVG arc command: A rx ry x-axis-rotation large-arc-flag sweep-flag x y
      // For left panel top-left arc: sweep=0 (counter-clockwise)
      pathParts.push(`A ${fmt(wrapRadius)} ${fmt(wrapRadius)} 0 0 0 ${fmt(localEndX)},${fmt(localEndY)}`);
    }
  }

  // Straight section (if item intersects it)
  if (intersectsStraight) {
    const straightStartY = Math.max(itemBounds.top, topArcBottomY);
    const straightEndY = Math.min(itemBounds.bottom, bottomArcTopY);

    if (straightEndY > straightStartY) {
      const localLeftEdgeX = clampX(toLocalX(leftEdge));
      const localStartY = clampY(toLocalY(straightStartY));
      const localEndY = clampY(toLocalY(straightEndY));

      // Line to start of straight section
      pathParts.push(`L ${fmt(localLeftEdgeX)},${fmt(localStartY)}`);
      // Line to end of straight section
      pathParts.push(`L ${fmt(localLeftEdgeX)},${fmt(localEndY)}`);
    }
  }

  // Bottom arc (if item intersects it)
  if (intersectsBottomArc) {
    const arcStartY = Math.max(itemBounds.top, bottomArcTopY);
    const arcEndY = Math.min(itemBounds.bottom, bottomArcBottomY);

    if (arcEndY > arcStartY) {
      const arcCenterX = dockLeft + scaledCornerRadius;
      const arcCenterY = dockBottom - scaledCornerRadius;

      // Start point of bottom arc
      const startX = getArcXAtYForPath(arcStartY, arcCenterX, arcCenterY, wrapRadius, 'left');
      const localStartX = clampX(toLocalX(startX));
      const localStartY = clampY(toLocalY(arcStartY));
      pathParts.push(`L ${fmt(localStartX)},${fmt(localStartY)}`);

      // End point of bottom arc
      const endX = getArcXAtYForPath(arcEndY, arcCenterX, arcCenterY, wrapRadius, 'left');
      const localEndX = clampX(toLocalX(endX));
      const localEndY = clampY(toLocalY(arcEndY));

      // SVG arc: sweep=0 (counter-clockwise for left panel)
      pathParts.push(`A ${fmt(wrapRadius)} ${fmt(wrapRadius)} 0 0 0 ${fmt(localEndX)},${fmt(localEndY)}`);
    }
  }

  // BOTTOM SECTION: Complete the path
  if (itemBounds.bottom > floorY) {
    // Item extends below cutout - go to full width then down
    const localFloorY = clampY(toLocalY(floorY));
    const localBottomArcEndX = clampX(toLocalX(bottomArcEndX));

    pathParts.push(`L ${fmt(localBottomArcEndX)},${fmt(localFloorY)}`);
    pathParts.push(`L ${fmt(itemW)},${fmt(localFloorY)}`);
    pathParts.push(`L ${fmt(itemW)},${fmt(itemH)}`);
  } else {
    // Item ends within cutout zone - extend to right edge at item bottom
    pathParts.push(`L ${fmt(itemW)},${fmt(itemH)}`);
  }

  // Complete the rectangle
  pathParts.push(`L 0,${fmt(itemH)}`);
  pathParts.push('Z');

  return {
    clipPath: `path('${pathParts.join(' ')}')`,
    overlapsWithCutout: true,
    zone: 'cutout',
  };
}

/**
 * Calculate clip-path for right panel (Gauge) items
 * Clips the LEFT side of the item to follow the cutout
 */
function calculateRightPanelPath(
  itemBounds: ItemBounds,
  geometry: CutoutGeometry,
  itemW: number,
  itemH: number,
  toLocalX: (x: number) => number,
  toLocalY: (y: number) => number,
  clampX: (x: number) => number,
  clampY: (y: number) => number,
  fmt: (n: number) => string,
  intersectsTopArc: boolean,
  intersectsStraight: boolean,
  intersectsBottomArc: boolean,
  topArcTopY: number,
  topArcBottomY: number,
  bottomArcTopY: number,
  bottomArcBottomY: number
): ClipPathResult {
  const {
    shelfY, floorY, rightEdge, wrapRadius, scaledCornerRadius, dockRight, dockTop, dockBottom,
  } = geometry;

  // Arc tangent points in container coordinates (matching ContentPanels.tsx)
  const topArcStartX = dockRight - scaledCornerRadius;  // On shelf
  const bottomArcEndX = dockRight - scaledCornerRadius;  // On floor

  const pathParts: string[] = [];

  // For right panel, we clip the LEFT side
  // Start at top-left, which may be on the cutout boundary

  if (itemBounds.top < shelfY) {
    // Item starts above cutout - full width at top
    pathParts.push(`M 0,0`);
    pathParts.push(`L ${fmt(itemW)},0`);
  } else {
    // Item starts within cutout zone - start on cutout boundary
    const startY = itemBounds.top;
    let startX: number;

    if (startY < topArcBottomY) {
      startX = getArcXAtYForPath(startY, dockRight - scaledCornerRadius, dockTop + scaledCornerRadius, wrapRadius, 'right');
    } else if (startY > bottomArcTopY) {
      startX = getArcXAtYForPath(startY, dockRight - scaledCornerRadius, dockBottom - scaledCornerRadius, wrapRadius, 'right');
    } else {
      startX = rightEdge;
    }

    const localStartX = clampX(toLocalX(startX));
    pathParts.push(`M ${fmt(localStartX)},0`);
    pathParts.push(`L ${fmt(itemW)},0`);
  }

  // RIGHT EDGE: Go down to bottom
  pathParts.push(`L ${fmt(itemW)},${fmt(itemH)}`);

  // BOTTOM EDGE: Go left
  if (itemBounds.bottom > floorY) {
    // Item extends below cutout - bottom-left corner at full width first
    pathParts.push(`L 0,${fmt(itemH)}`);

    // Go up to floor level
    const localFloorY = clampY(toLocalY(floorY));
    pathParts.push(`L 0,${fmt(localFloorY)}`);

    // Horizontal to bottom arc end point
    const localBottomArcEndX = clampX(toLocalX(bottomArcEndX));
    pathParts.push(`L ${fmt(localBottomArcEndX)},${fmt(localFloorY)}`);
  } else {
    // Item ends within cutout zone
    const endY = itemBounds.bottom;
    let endX: number;

    if (endY > bottomArcTopY) {
      endX = getArcXAtYForPath(endY, dockRight - scaledCornerRadius, dockBottom - scaledCornerRadius, wrapRadius, 'right');
    } else if (endY < topArcBottomY) {
      endX = getArcXAtYForPath(endY, dockRight - scaledCornerRadius, dockTop + scaledCornerRadius, wrapRadius, 'right');
    } else {
      endX = rightEdge;
    }

    const localEndX = clampX(toLocalX(endX));
    pathParts.push(`L ${fmt(localEndX)},${fmt(itemH)}`);
  }

  // CUTOUT EDGE: Trace UP along the cutout boundary with arcs

  // Bottom arc (going up, if item intersects it)
  if (intersectsBottomArc) {
    const arcEndY = Math.min(itemBounds.bottom, bottomArcBottomY);
    const arcStartY = Math.max(itemBounds.top, bottomArcTopY);

    if (arcEndY > arcStartY) {
      const arcCenterX = dockRight - scaledCornerRadius;
      const arcCenterY = dockBottom - scaledCornerRadius;

      // End point (bottom of arc in this item)
      const endX = getArcXAtYForPath(arcEndY, arcCenterX, arcCenterY, wrapRadius, 'right');
      const localEndX = clampX(toLocalX(endX));
      const localEndY = clampY(toLocalY(arcEndY));
      pathParts.push(`L ${fmt(localEndX)},${fmt(localEndY)}`);

      // Start point (top of arc, going up)
      const startX = getArcXAtYForPath(arcStartY, arcCenterX, arcCenterY, wrapRadius, 'right');
      const localStartX = clampX(toLocalX(startX));
      const localStartY = clampY(toLocalY(arcStartY));

      // SVG arc: sweep=0 for going up on right side (counter-clockwise)
      pathParts.push(`A ${fmt(wrapRadius)} ${fmt(wrapRadius)} 0 0 0 ${fmt(localStartX)},${fmt(localStartY)}`);
    }
  }

  // Straight section (going up)
  if (intersectsStraight) {
    const straightEndY = Math.min(itemBounds.bottom, bottomArcTopY);
    const straightStartY = Math.max(itemBounds.top, topArcBottomY);

    if (straightEndY > straightStartY) {
      const localRightEdgeX = clampX(toLocalX(rightEdge));
      const localEndY = clampY(toLocalY(straightEndY));
      const localStartY = clampY(toLocalY(straightStartY));

      pathParts.push(`L ${fmt(localRightEdgeX)},${fmt(localEndY)}`);
      pathParts.push(`L ${fmt(localRightEdgeX)},${fmt(localStartY)}`);
    }
  }

  // Top arc (going up, if item intersects it)
  if (intersectsTopArc) {
    const arcEndY = Math.min(itemBounds.bottom, topArcBottomY);
    const arcStartY = Math.max(itemBounds.top, topArcTopY);

    if (arcEndY > arcStartY) {
      const arcCenterX = dockRight - scaledCornerRadius;
      const arcCenterY = dockTop + scaledCornerRadius;

      // Bottom of arc
      const endX = getArcXAtYForPath(arcEndY, arcCenterX, arcCenterY, wrapRadius, 'right');
      const localEndX = clampX(toLocalX(endX));
      const localEndY = clampY(toLocalY(arcEndY));
      pathParts.push(`L ${fmt(localEndX)},${fmt(localEndY)}`);

      // Top of arc (going up)
      const startX = getArcXAtYForPath(arcStartY, arcCenterX, arcCenterY, wrapRadius, 'right');
      const localStartX = clampX(toLocalX(startX));
      const localStartY = clampY(toLocalY(arcStartY));

      // SVG arc: sweep=0 for going up (counter-clockwise)
      pathParts.push(`A ${fmt(wrapRadius)} ${fmt(wrapRadius)} 0 0 0 ${fmt(localStartX)},${fmt(localStartY)}`);
    }
  }

  // TOP SECTION: Complete path back to start
  if (itemBounds.top < shelfY) {
    const localShelfY = clampY(toLocalY(shelfY));
    const localTopArcStartX = clampX(toLocalX(topArcStartX));

    pathParts.push(`L ${fmt(localTopArcStartX)},${fmt(localShelfY)}`);
    pathParts.push(`L 0,${fmt(localShelfY)}`);
    pathParts.push(`L 0,0`);
  }

  pathParts.push('Z');

  return {
    clipPath: `path('${pathParts.join(' ')}')`,
    overlapsWithCutout: true,
    zone: 'cutout',
  };
}

/**
 * Get X coordinate on an arc at a given Y position
 * Used for calculating points along the curved cutout boundary
 */
function getArcXAtYForPath(
  y: number,
  arcCenterX: number,
  arcCenterY: number,
  radius: number,
  side: 'left' | 'right'
): number {
  const dy = y - arcCenterY;
  if (Math.abs(dy) >= radius) {
    // Y is outside arc range - return tangent point
    return side === 'left' ? arcCenterX - radius : arcCenterX + radius;
  }
  const dx = Math.sqrt(radius * radius - dy * dy);
  return side === 'left' ? arcCenterX - dx : arcCenterX + dx;
}
